import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Building2, MapPin, Layers, ChevronDown, ChevronRight, Lock, Clock, AlertOctagon, Sparkles } from "lucide-react";
import api from "../../lib/api";

/**
 * HierarchyPanel — calm collapsible org tree with governance status indicators.
 *
 * v3.8.1 — Trust & Clarity
 *   • Cascaded lock visualization: each node shows BOTH its own governance
 *     state AND a faint indicator when an ancestor lock cascades into it.
 *     An operator can now scan the tree and instantly see governance reach
 *     without clicking each node.
 *   • Vocabulary normalization: every reference to the Sparkles indicator
 *     is now "Local override" (not "Local" / not "Override").
 *   • Layout: legend moved below the eyebrow so it never collides with
 *     the heading at narrow widths.
 *
 * Implementation note: rather than rendering a recursive JSX component
 * (which can trip JSX/Babel transformers on some build chains), we
 * flatten the tree into an indexed list with `depth` markers and render
 * a single map. This is faster, more accessible, and entirely avoids
 * self-referential component patterns.
 */
export const HierarchyPanel = ({ selectedOrgId, onSelectOrg }) => {
    const { t } = useTranslation();
    const [tree, setTree] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [collapsed, setCollapsed] = useState({});

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get("/operational-base/hierarchy");
                if (!cancelled) setTree(res.data?.hierarchy || []);
            } catch (err) {
                if (!cancelled) setError(err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Flatten the tree into an ordered list of {node, depth, hasChildren, hidden}.
    const flat = useMemo(() => {
        const out = [];
        const walk = (n, depth, parentChain) => {
            const collapsedHere = !!collapsed[n.id];
            const hidden = parentChain.some((pid) => collapsed[pid]);
            const hasChildren = !!(n.children && n.children.length);
            out.push({
                id: n.id,
                node: n,
                depth,
                hasChildren,
                isCollapsed: collapsedHere,
                hidden,
            });
            if (hasChildren) {
                for (const c of n.children) {
                    walk(c, depth + 1, [...parentChain, n.id]);
                }
            }
        };
        for (const root of tree) walk(root, 0, []);
        return out;
    }, [tree, collapsed]);

    const toggle = (id) => setCollapsed((s) => ({ ...s, [id]: !s[id] }));

    if (loading) {
        return (
            <p className="text-sm" style={{ color: "var(--ob-text-tertiary)" }} data-testid="ob-hierarchy-loading">
                {t("ob.activity.loading")}
            </p>
        );
    }
    if (error || tree.length === 0) {
        return (
            <p className="text-sm" style={{ color: "var(--ob-text-tertiary)" }} data-testid="ob-hierarchy-empty">
                {t("ob.hierarchy.empty")}
            </p>
        );
    }

    return (
        <div className="ob-card p-5" data-testid="ob-hierarchy-panel">
            <header className="mb-4">
                <p className="ob-eyebrow">{t("ob.hierarchy.eyebrow")}</p>
                <h3 className="text-lg" style={{ color: "var(--ob-text-primary)", fontFamily: "Outfit, sans-serif" }}>
                    {t("ob.hierarchy.title")}
                </h3>
                <Legend />
            </header>
            <ul className="space-y-1" data-testid="ob-hierarchy-tree">
                {flat.map((row) => {
                    if (row.hidden) return null;
                    const n = row.node;
                    const Icon = n.org_type === "corporate" ? Building2 : n.org_type === "regional" ? Layers : MapPin;
                    const isSelected = selectedOrgId === n.id;
                    return (
                        <li key={n.id}>
                            <div
                                data-testid={"ob-hierarchy-node-" + n.id}
                                data-org-type={n.org_type}
                                data-selected={isSelected ? "true" : "false"}
                                className="flex items-center gap-2 rounded-xl px-2 py-2 cursor-pointer"
                                style={{
                                    background: isSelected ? "rgba(16,185,129,0.10)" : "transparent",
                                    color: isSelected ? "var(--ob-text-primary)" : "var(--ob-text-secondary)",
                                    paddingLeft: (0.5 + row.depth * 1.25) + "rem",
                                }}
                                onClick={() => onSelectOrg && onSelectOrg(n)}
                            >
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); if (row.hasChildren) toggle(n.id); }}
                                    aria-label={row.isCollapsed ? "Expand" : "Collapse"}
                                    className="p-0.5 rounded hover:bg-white/5"
                                    style={{ visibility: row.hasChildren ? "visible" : "hidden" }}
                                    data-testid={"ob-hierarchy-toggle-" + n.id}
                                >
                                    {row.isCollapsed
                                        ? <ChevronRight size={14} strokeWidth={1.5} aria-hidden="true" />
                                        : <ChevronDown size={14} strokeWidth={1.5} aria-hidden="true" />}
                                </button>
                                <Icon size={14} strokeWidth={1.5} aria-hidden="true" />
                                <span className="text-sm flex-1 truncate" style={{ fontFamily: "Manrope, sans-serif" }}>
                                    {n.name}
                                </span>
                                <Badges node={n} />
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

const Badges = ({ node }) => {
    const { t } = useTranslation();
    const items = [];
    if (node.lock_count > 0) {
        items.push({
            icon: Lock,
            color: "#93C5FD",
            tone: "owned",
            label: t("ob.hierarchy.badge.locks", { count: node.lock_count }),
            key: "lock",
            testId: "ob-hierarchy-badge-lock",
        });
    }
    // v3.8.1 — Cascaded lock visibility. Faint indicator when an ancestor
    // owns a lock that reaches this node, so an operator scanning the
    // hierarchy sees governance reach without clicking through every node.
    if (node.inherited_lock_count > 0 && (node.lock_count || 0) === 0) {
        items.push({
            icon: Lock,
            color: "#93C5FD",
            tone: "inherited",
            label: t("ob.hierarchy.badge.inherited_lock", {
                count: node.inherited_lock_count,
                origin: node.inherited_lock_origin || "",
            }),
            key: "ilock",
            testId: "ob-hierarchy-badge-inherited-lock",
        });
    }
    if (node.scheduled_count > 0) {
        items.push({
            icon: Clock,
            color: "#93C5FD",
            tone: "owned",
            label: t("ob.hierarchy.badge.scheduled", { count: node.scheduled_count }),
            key: "sched",
            testId: "ob-hierarchy-badge-scheduled",
        });
    }
    if (node.break_glass_count > 0) {
        items.push({
            icon: AlertOctagon,
            color: "#FCA5A5",
            tone: "owned",
            label: t("ob.hierarchy.badge.break_glass", { count: node.break_glass_count }),
            key: "bg",
            testId: "ob-hierarchy-badge-break-glass",
        });
    }
    if (node.has_local_override) {
        items.push({
            icon: Sparkles,
            color: "#A7F3D0",
            tone: "owned",
            label: t("ob.hierarchy.badge.local_override"),
            key: "local",
            testId: "ob-hierarchy-badge-local",
        });
    }
    if (items.length === 0) return null;
    return (
        <div className="flex items-center gap-1.5 flex-shrink-0">
            {items.map((b) => {
                const I = b.icon;
                const isInherited = b.tone === "inherited";
                return (
                    <span
                        key={b.key}
                        title={b.label}
                        data-testid={b.testId}
                        data-tone={b.tone}
                        className="inline-flex items-center justify-center w-6 h-6 rounded-md"
                        style={{
                            background: b.color + (isInherited ? "0D" : "1A"),
                            color: b.color,
                            opacity: isInherited ? 0.55 : 1,
                            border: isInherited ? `1px dashed ${b.color}40` : "none",
                        }}
                        aria-label={b.label}
                    >
                        <I size={12} strokeWidth={1.5} aria-hidden="true" />
                    </span>
                );
            })}
        </div>
    );
};

const Legend = () => {
    const { t } = useTranslation();
    const items = [
        { icon: Lock, color: "#93C5FD", label: t("ob.hierarchy.legend.lock") },
        { icon: Clock, color: "#93C5FD", label: t("ob.hierarchy.legend.scheduled") },
        { icon: AlertOctagon, color: "#FCA5A5", label: t("ob.hierarchy.legend.break_glass") },
        { icon: Sparkles, color: "#A7F3D0", label: t("ob.hierarchy.legend.local_override") },
    ];
    return (
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs" style={{ color: "var(--ob-text-tertiary)" }}>
            {items.map((it) => {
                const I = it.icon;
                return (
                    <span key={it.label} className="inline-flex items-center gap-1">
                        <I size={10} strokeWidth={1.5} style={{ color: it.color }} aria-hidden="true" />
                        <span>{it.label}</span>
                    </span>
                );
            })}
            <span
                className="inline-flex items-center gap-1"
                title={t("ob.hierarchy.legend.inherited_hint")}
                data-testid="ob-hierarchy-legend-inherited"
            >
                <Lock size={10} strokeWidth={1.5} style={{ color: "#93C5FD", opacity: 0.55 }} aria-hidden="true" />
                <span style={{ opacity: 0.75 }}>{t("ob.hierarchy.legend.inherited")}</span>
            </span>
        </div>
    );
};
